import { Error } from '../error';
import { Result } from '../types';

export type AsyncStatus =
  'idle' |
  'loading' |
  'error' |
  'success';

/** Async version of ResultTuple, which returns a Promise. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any` defaults are required for Result/AsyncResult to allow flexible tuple typing
export type AsyncResult<TResult = any, TError = any> =
  Promise<Result<TResult, TError>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any` defaults needed for generic async task interface
export interface IAsyncTask<TResult = any, TError = any> {

  readonly isActive: boolean;
  error: TError;
  result?: TResult;

  start(): AsyncResult<TResult, TError>;
}

export type PromiseType<T> = T extends PromiseLike<infer U> ? U : T;

/**
 * Utility for returning a Promise that resolves after the provided milliseconds.
 */
export async function sleep<T>(timeout: number, val?: T): Promise<T> {
  return new Promise((res) => {
    setTimeout(() => res(val!), timeout);
  });
}

/**
 * Wraps an async method into a try catch block which will return an errored AsyncResult if any error occurs.
 */
export async function checked<TResult, TError>(fn: () => AsyncResult<TResult, TError>): AsyncResult<TResult, unknown> {

  try {
    return await fn();
  } catch (err) {

    if (import.meta.env.DEV) {
      console.error('Checked function call failed! ', err);
    }

    return [null, err];
  }
}

export async function timed<TResult, TError = unknown>(
  fn: () => AsyncResult<TResult, TError>,
  timeout: number
): AsyncResult<TResult, TError | Error> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise: AsyncResult<TResult, TError | Error> = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      const err = new Error('Timeout', `The requested operation timed out.`);
      resolve([null, err]);
    }, timeout);
  });

  const resultPromise = fn().then((res) => {
    clearTimeout(timeoutId);
    return res;
  });

  return Promise.race([timeoutPromise, resultPromise]);
}
